import type { MouseEvent, ReactNode } from "react";
import { createPortal } from "react-dom";

import { AnimatePresence, motion, type Variants } from "framer-motion";
import { tv, type VariantProps } from "tailwind-variants";

import {
	cn,
	type UseIsOpenReturn,
	type VariantSlotsToClassNames,
} from "@virtual-brain-lib/frontend-design-system/utils";

import { Button, type ButtonProps } from "./Button";
import { Icon, type Icons } from "./Icon";
import { Span } from "./Span";

const modalVariants = tv({
	slots: {
		base: "relative flex max-h-full w-full flex-col rounded-lg bg-secondary px-2 shadow-2xl",
		backdrop: "fixed inset-0 z-50 flex items-center justify-center bg-on-primary-light/50 p-4",
		header: "flex cursor-default items-center justify-between rounded-t-lg bg-secondary px-4",
		body: "overflow-y-auto rounded-t-lg border-x border-t border-primary bg-white",
		footer: "mt-auto mb-2 flex items-center justify-end gap-3 rounded-b-lg border-x border-b border-primary bg-background px-4 py-3",
	},
	variants: {
		size: {
			sm: {
				base: "w-md max-w-md",
				body: "p-4",
				header: "py-2",
			},
			md: {
				base: "w-lg max-w-lg",
				body: "p-4",
				header: "py-2",
			},
			lg: {
				base: "w-2xl max-w-2xl",
				body: "p-4",
				header: "py-2",
			},
			xl: {
				base: "w-4xl max-w-4xl",
				body: "p-4",
				header: "py-2",
			},
			"2xl": {
				base: "w-6xl max-w-6xl",
				body: "p-4",
				header: "py-2",
			},
			full: {
				base: "h-full w-full max-w-full",
				body: "p-4",
				header: "py-2",
			},
		},
	},
});

type ModalVariants = VariantProps<typeof modalVariants>;

type FooterButtonProps = Pick<
	ButtonProps,
	| "variant"
	| "color"
	| "onClick"
	| "leftIcon"
	| "disabled"
	| "tooltip"
	| "hidden"
	| "type"
	| "form"
> & {
	text?: string;
};

export type ModalProps = ModalVariants &
	Pick<UseIsOpenReturn, "isOpen" | "close"> & {
		readonly title?: string;
		readonly titleRightComponent?: ReactNode;
		readonly backButton?: { onClick?: () => void };
		readonly icon?: Icons;
		readonly children: ReactNode;
		readonly footer?: {
			secondaryButton?: FooterButtonProps;
			primaryButton?: FooterButtonProps;
		};
		readonly classNames?: VariantSlotsToClassNames<typeof modalVariants>;
		isClosableOnOutsideClick?: boolean;
	};

const backdropVariants = {
	hidden: { opacity: 0 },
	visible: { opacity: 1 },
};

const modalTransitionVariants: Variants = {
	hidden: {
		opacity: 0,
		scale: 0.95,
		y: -20,
	},
	visible: {
		opacity: 1,
		scale: 1,
		y: 0,
		transition: {
			type: "spring",
			duration: 0.3,
			bounce: 0.3,
		},
	},
	exit: {
		opacity: 0,
		scale: 0.95,
		y: -20,
		transition: {
			duration: 0.2,
		},
	},
};

const defaultFooter: ModalProps["footer"] = {
	secondaryButton: {},
};

// eslint-disable-next-line complexity
export function Modal({
	isOpen,
	close,
	title,
	titleRightComponent,
	backButton,
	icon,
	size = "md",
	children,
	footer = defaultFooter,
	classNames,
	isClosableOnOutsideClick = true,
}: ModalProps) {
	const { base, backdrop, header, body, footer: footerClassName } = modalVariants({ size });
	const { secondaryButton = defaultFooter?.secondaryButton, primaryButton } = footer ?? {};
	const handleBackdropMouseDown = (e: MouseEvent<HTMLDivElement>) => {
		if (e.target === e.currentTarget && isClosableOnOutsideClick) {
			close();
		}
	};

	const hasFooter =
		(secondaryButton && !secondaryButton.hidden) || (primaryButton && !primaryButton.hidden);
	const hasHeader = Boolean(title ?? icon ?? titleRightComponent ?? backButton);

	return createPortal(
		<AnimatePresence mode="wait">
			{isOpen && (
				<motion.div
					className={backdrop({ className: classNames?.backdrop })}
					variants={backdropVariants}
					initial="hidden"
					animate="visible"
					exit="hidden"
					onMouseDown={handleBackdropMouseDown}
				>
					<motion.div
						className={base({ className: classNames?.base })}
						variants={modalTransitionVariants}
						initial="hidden"
						animate="visible"
						exit="exit"
						onClick={(e) => {
							e.stopPropagation();
						}}
					>
						{hasHeader && (
							<div className={header({ className: classNames?.header })}>
								<div className="flex items-center gap-2">
									{backButton?.onClick && (
										<Button
											variant="ghost"
											size="sm"
											color="primary"
											leftIcon="arrowLeft"
											onClick={backButton.onClick}
										/>
									)}
									{icon && <Icon name={icon} color="primary" size="lg" />}
									{title && (
										<Span
											color="primary"
											variant="body-md"
											className="font-semibold"
										>
											{title}
										</Span>
									)}
									{titleRightComponent}
								</div>
								<Button
									variant="ghost"
									size="sm"
									color="primary"
									leftIcon="x"
									onClick={close}
								/>
							</div>
						)}

						<div
							className={body({
								className: cn(
									classNames?.body,
									!hasFooter && "mb-2 rounded-b-lg border-x border-b pb-0",
									!hasHeader && "mt-2",
								),
							})}
						>
							{children}
						</div>

						{hasFooter && (
							<div className={footerClassName({ className: classNames?.footer })}>
								{secondaryButton && !secondaryButton.hidden && (
									<Button
										type="button"
										leftIcon="x"
										variant="ghost"
										color="danger"
										onClick={close}
										{...secondaryButton}
									>
										{secondaryButton.text ?? "Cancel"}
									</Button>
								)}
								{primaryButton && !primaryButton.hidden && (
									<Button type="button" variant="solid" {...primaryButton}>
										{primaryButton.text}
									</Button>
								)}
							</div>
						)}
					</motion.div>
				</motion.div>
			)}
		</AnimatePresence>,
		document.body,
	);
}
